import { Link } from 'react-router-dom'
import { Brand, ThemeToggle } from '../components/Bits'
import { useSeo } from '../lib/seo'

/**
 * Юридические страницы: пользовательское соглашение (оферта) и реквизиты.
 * Оферта обязана существовать до приёма платежей, поэтому эти страницы
 * публичные и индексируемые, а ссылка на оферту стоит прямо у кнопки оплаты.
 *
 * Обе страницы живут на одном компоненте: структура одинаковая — шапка,
 * документ, подвал со ссылками на соседние документы.
 */
function LegalPage({ title, edition, children }: { title: string; edition: string; children: React.ReactNode }) {
  return (
    <div className="wrap col" style={{ gap: 20 }}>
      <header className="row spread row-wrap">
        <Brand />
        <ThemeToggle />
      </header>

      <article className="legal col">
        <Link className="account-login" to="/">
          ← На главную clapme
        </Link>
        <h1 style={{ margin: 0, fontSize: 30 }}>{title}</h1>
        <p className="muted tiny">{edition}</p>
        {children}
      </article>

      <footer className="home-footer">
        <span>
          clapme.ru · <Link to="/legal/agreement">Пользовательское соглашение</Link> ·{' '}
          <Link to="/legal/requisites">Реквизиты</Link>
        </span>
      </footer>
    </div>
  )
}

export function Agreement() {
  useSeo({ title: 'Пользовательское соглашение (оферта) — clapme', canonical: '/legal/agreement', noindex: false })

  return (
    <LegalPage title="Пользовательское соглашение (публичная оферта)" edition="Редакция от 30 октября 2026 г.">
      <p>
        Настоящий документ является официальным публичным предложением (офертой) <strong>Мерец Артем
        Леонидович</strong> (далее — «Исполнитель»), применяющего специальный налоговый режим «Налог на
        профессиональный доход» (самозанятость), и определяет условия использования сервиса{' '}
        <strong>clapme.ru</strong> и его поддоменов (далее — «Сервис»), включая приобретение платных
        тарифов.
      </p>
      <p>
        Использование Сервиса, а также оплата тарифа означают полное согласие с условиями настоящей
        оферты (акцепт). Оферта вступает в силу с момента акцепта и имеет силу договора.
      </p>

      <h2>1. Термины</h2>
      <ul>
        <li><strong>Сервис</strong> — сайт clapme.ru: интерактив для живых мероприятий (опросы, реакции,
          свободные ответы) с экранами ведущего, проектора и участников.</li>
        <li><strong>Эфир</strong> — разовый сеанс взаимодействия с аудиторией по коду доступа.</li>
        <li><strong>Демонстрация</strong> — заготовка презентации, сохранённая в аккаунте Пользователя
          и запускаемая в эфир.</li>
        <li><strong>Тариф</strong> — платный доступ к возможностям Сервиса на оговорённый срок и в
          оговорённых лимитах (число демонстраций, слайдов, участников зала).</li>
        <li><strong>Пользователь</strong> — физическое лицо, использующее Сервис; <strong>Ведущий</strong> —
          Пользователь, создающий эфиры и демонстрации; <strong>Участник</strong> — лицо, отвечающее в эфире.</li>
      </ul>

      <h2>2. Предмет</h2>
      <p>
        Исполнитель предоставляет Пользователю доступ к функциональности Сервиса на условиях настоящей
        оферты. Базовые функции доступны бесплатно; тарифы расширяют лимиты. Сервис является
        программным обеспечением, не имеющим материального носителя, и предоставляется в электронном виде.
      </p>

      <h2>3. Цена и оплата</h2>
      <ul>
        <li>Цены указаны в российских рублях и отображаются на странице тарифов до подтверждения оплаты.</li>
        <li>Оплата производится через платёжный сервис <strong>ЮKassa</strong> (АО «ЮKassa»); Исполнитель
          не получает и не хранит данные банковских карт Пользователя.</li>
        <li>Получателем денежных средств является Исполнитель — самозанятый с <strong>ИНН
          860319007339</strong>. Платёж зачисляется на счёт, привязанный к указанному ИНН.</li>
        <li>Моментом оплаты считается подтверждение успешного платежа платёжным сервисом. Чек по каждой
          оплате формируется в соответствии с применяемым налоговым режимом (сервис ФНС «Мой налог»).</li>
      </ul>

      <h2>4. Момент исполнения</h2>
      <p>
        Доступ к возможностям оплаченного тарифа предоставляется автоматически и немедленно после
        подтверждения платежа. Услуга оказывается в течение всего оплаченного срока тарифа путём
        предоставления доступа к соответствующим функциям и лимитам Сервиса.
      </p>

      <h2>5. Возврат</h2>
      <p>
        Услуга носит нематериальный характер и предоставляется немедленно после оплаты; доступ к
        возможностям тарифа надлежащего качества <strong>возврату и обмену не подлежит</strong>, поскольку
        используется Пользователем с первого дня (ст. 26.1 Закона РФ «О защите прав потребителей»).
      </p>
      <p>
        Если платёж списан, а тариф не активировался из-за технического сбоя, Пользователь вправе
        обратиться на <a href="mailto:support@slowlane.ru">support@slowlane.ru</a> с указанием даты, суммы и
        идентификатора платежа: Исполнитель активирует тариф либо вернёт денежные средства тем же
        способом, которым была произведена оплата.
      </p>

      <h2>6. Аккаунты и вход</h2>
      <p>
        Персональные функции (демонстрации, тарифы) привязаны к аккаунту входа через Яндекс ID.
        Исполнитель не несёт ответственности за утрату доступа, вызванную потерей Пользователем доступа
        к своему аккаунту у провайдера входа, а также не отвечает за действия провайдера входа.
      </p>

      <h2>7. Ответственность, «как есть» и отсутствие SLA</h2>
      <p>
        Сервис и программное обеспечение предоставляются на условиях «как есть» (as is) и «как
        доступно» (as available). <strong>Опубликованного соглашения об уровне обслуживания (SLA) не
        существует</strong>: Исполнитель не гарантирует бесперебойную работу, отсутствие ошибок и сбоев,
        сохранение неизменного состава функций, скорость восстановления после сбоев и совместимость с
        любым оборудованием и сетями Пользователей.
      </p>
      <ul>
        <li>Возможны технические перерывы, обновления, временное или полное отсутствие отдельных функций;
          функции могут изменяться или удаляться.</li>
        <li>Эфиры, демонстрации и их содержимое могут быть утеряны при сбоях; Исполнитель прилагает
          разумные усилия к сохранности, но не гарантирует её абсолютно.</li>
        <li>Быстрые эфиры без регистрации живут ограниченное время и удаляются автоматически — это
          штатное поведение, описанное в документации, а не недостаток услуги.</li>
        <li>За содержание вопросов, ответов и иной информации, которую Ведущий доносит до аудитории через
          Сервис, отвечает Ведущий. Исполнитель не проверяет и не одобряет такое содержание заранее.</li>
        <li>Исполнитель вправе ограничивать интенсивность обращений к Сервису (лимиты частоты и объёма),
          отклонять создание эфиров и демонстраций, нарушающих ограничения или создавших нагрузку,
          угрожающую работе Сервиса, и блокировать аккаунты при злоупотреблениях.</li>
      </ul>
      <p>
        Исполнитель не несёт ответственности за убытки, включая упущенную выгоду и непрямые убытки,
        возникшие в результате использования или невозможности использования Сервиса, за исключением
        случаев, прямо предусмотренных законодательством РФ. Совокупная ответственность Исполнителя по
        настоящей оферте ограничена суммой, фактически уплаченной Пользователем за текущий оплаченный
        период тарифа.
      </p>

      <h2>8. Данные</h2>
      <p>
        Вход выполняется через Яндекс ID; технические идентификаторы (cookie сессии, локальное хранилище
        браузера участника, обезличенная статистика Яндекс Метрики) используются для работы Сервиса.
        Ответы участников и итоги эфиров сохраняются в объёме, необходимом для показа результатов и
        истории запусков. Обезличенная статистика собирается в агрегированном виде.
      </p>

      <h2>9. Изменение условий и тарифов</h2>
      <p>
        Исполнитель вправе в одностороннем порядке изменять условия оферты, тарифы, лимиты и состав
        функций, публикуя новую редакцию на этой странице. Продолжение использования Сервиса после
        публикации означает согласие с новой редакцией. Оплаченный период действует на условиях,
        действовавших на момент оплаты, если иное не требуется по закону.
      </p>

      <h2>10. Прочее</h2>
      <p>
        К оферте применяется законодательство РФ. Спор разрешается путём обращения на{' '}
        <a href="mailto:support@slowlane.ru">support@slowlane.ru</a>; при невозможности урегулирования — в
        соответствии с законом. Недействительность отдельного положения не влечёт недействительности
        оферты в целом. Актуальные реквизиты Исполнителя — на странице{' '}
        <Link to="/legal/requisites">Реквизиты</Link>.
      </p>
    </LegalPage>
  )
}

export function Requisites() {
  useSeo({ title: 'Реквизиты — clapme', canonical: '/legal/requisites', noindex: false })

  return (
    <LegalPage title="Реквизиты" edition="Актуально на 30 октября 2026 г.">
      <p>
        Продавцом и оператором сервиса <strong>clapme.ru</strong> (интерактив для живых мероприятий) является:
      </p>

      <table className="req-table">
        <tbody>
          <tr><td>Наименование</td><td><strong>Мерец Артем Леонидович</strong></td></tr>
          <tr><td>Статус</td><td>Плательщик налога на профессиональный доход (самозанятый)</td></tr>
          <tr><td>ИНН</td><td><strong>860319007339</strong></td></tr>
          <tr><td>E-mail</td><td><a href="mailto:support@slowlane.ru">support@slowlane.ru</a></td></tr>
          <tr><td>Сайт</td><td><a href="https://clapme.ru">clapme.ru</a></td></tr>
          <tr><td>Приём платежей</td><td>ЮKassa (АО «ЮKassa»)</td></tr>
          <tr><td>Получатель платежей</td><td>Самозанятый, ИНН <strong>860319007339</strong> — на этот ИНН зачисляются платежи</td></tr>
        </tbody>
      </table>

      <h2>Документы</h2>
      <ul>
        <li><Link to="/legal/agreement">Пользовательское соглашение (оферта)</Link></li>
      </ul>

      <p>
        По вопросам оплат, возвратов и работы Сервиса пишите на{' '}
        <a href="mailto:support@slowlane.ru">support@slowlane.ru</a> — отвечаем в течение разумного срока.
      </p>
    </LegalPage>
  )
}
